/** @jsx jsx */

import { useState } from 'react';

import { jsx } from '@emotion/core';
import { Margin, Surface, Tabs, Text } from '@zapier/design-system';

import { useRouter } from 'app/developer-v3/hooks';
import { convertObjToKvps, getAppVersionUrl } from 'app/developer-v3/utils';

import Options from './Options';
import KeyValueEditor, { KeyValue } from '../KeyValueEditor';
import { TABS } from './constants';
import { disableKvps, getTabs } from './utils';

import type {
  RequestOptions,
  RequestSchema,
} from 'app/developer-v3/platformSchema/request';

type Props = {
  body?: Record<string, unknown>;
  disabledTabs?: Array<'body' | 'options'>;
  headers?: Record<string, unknown>;
  initialActiveTab?: number;
  isDisabledRequestTemplate?: boolean;
  onChangeBody: (items: Array<KeyValue>) => void;
  onChangeHeaders: (items: Array<KeyValue>) => void;
  onChangeOptions?: (name: string, value: boolean) => void;
  onChangeParams: (items: Array<KeyValue>) => void;
  params?: Record<string, unknown>;
  removeMissingValuesFrom?: RequestOptions;
  requestTemplate: RequestSchema | null;
};

const defaultProps = {
  body: {},
  disabledTabs: [],
  headers: {},
  initialActiveTab: 0,
  isDisabledRequestTemplate: false,
  onChangeOptions: () => {},
  params: {},
  removeMissingValuesFrom: {},
};

/*
 * This components merges the fields (params/headers/body/options) of a request
 * with a request template. There are individual callbacks on the merged fields.
 */
function RequestFields({
  body = defaultProps.body,
  disabledTabs = defaultProps.disabledTabs,
  headers = defaultProps.headers,
  initialActiveTab = defaultProps.initialActiveTab,
  isDisabledRequestTemplate = defaultProps.isDisabledRequestTemplate,
  onChangeBody,
  onChangeHeaders,
  onChangeOptions = defaultProps.onChangeOptions,
  onChangeParams,
  params = defaultProps.params,
  removeMissingValuesFrom = defaultProps.removeMissingValuesFrom,
  requestTemplate,
}: Props) {
  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const router = useRouter();
  const { id: integrationId, version } = router.params;

  const maybeDisableRequestTemplateKvps = (kvps: KeyValue[]) =>
    isDisabledRequestTemplate ? disableKvps(kvps) : kvps;

  const requestTemplateKvps = {
    params: maybeDisableRequestTemplateKvps(
      convertObjToKvps(requestTemplate?.params)
    ),
    headers: maybeDisableRequestTemplateKvps(
      convertObjToKvps(requestTemplate?.headers)
    ),
    body: maybeDisableRequestTemplateKvps(
      convertObjToKvps(requestTemplate?.body)
    ),
  };

  const paramKvps = convertObjToKvps(params);
  const headerKvps = convertObjToKvps(headers);
  const bodyKvps = convertObjToKvps(body);

  const allParams = [...requestTemplateKvps.params, ...paramKvps];
  const allHeaders = [...requestTemplateKvps.headers, ...headerKvps];
  const allBody = [...requestTemplateKvps.body, ...bodyKvps];

  return (
    <div>
      <Surface borderColor="GrayWarm4">
        <Margin margin="0px 10px 0px 10px">
          <Tabs
            items={getTabs(disabledTabs)}
            onChooseItem={(item: any) => setActiveTab(item.id)}
            selectedItemHref={`#${activeTab}`}
          />
        </Margin>
        <Margin margin="10px">
          {isDisabledRequestTemplate && (
            <Text margin="15px 0px" tag="p">
              Triggers and Actions inherit the Request Template fields that are
              defined and editable in{' '}
              <a
                href={`${getAppVersionUrl(
                  integrationId,
                  version
                )}/advanced/request-template`}
              >
                Advanced Settings
              </a>
            </Text>
          )}
          {activeTab === TABS.PARAMS && (
            <KeyValueEditor
              enableAutoComplete={true}
              initialItems={allParams}
              onUpdate={onChangeParams}
            />
          )}
          {activeTab === TABS.HEADERS && (
            <KeyValueEditor
              enableAutoComplete={true}
              initialItems={allHeaders}
              onUpdate={onChangeHeaders}
            />
          )}
          {activeTab === TABS.BODY && (
            <KeyValueEditor
              enableAutoComplete={true}
              initialItems={allBody}
              isDisabled={disabledTabs.includes('body')}
              onUpdate={onChangeBody}
            />
          )}
          {activeTab === TABS.OPTIONS && (
            <Options
              isDisabled={disabledTabs.includes('options')}
              onChangeOptions={onChangeOptions}
              removeMissingValuesFrom={removeMissingValuesFrom}
            />
          )}
        </Margin>
      </Surface>
    </div>
  );
}

RequestFields.defaultProps = defaultProps;

export default RequestFields;
